/**
 * Controlled Textarea with Debounced Update
 * Prevents parent re-renders on every keystroke by maintaining local state
 */

import React, { useState, useEffect, useRef } from 'react';

const ControlledTextarea = ({ 
  value, 
  onChange, 
  onBlur,
  debugMode = false,
  fieldName = 'unknown',
  ...props 
}) => {
  const [localValue, setLocalValue] = useState(value);
  const textareaRef = useRef(null);
  const timeoutRef = useRef(null);
  
  // Debug logging
  useEffect(() => {
    if (debugMode) {
      console.log(`[ControlledTextarea:${fieldName}] Mounted`);
    }
    return () => {
      if (debugMode) {
        console.log(`[ControlledTextarea:${fieldName}] Unmounted`);
      }
    };
  }, [debugMode, fieldName]);

  // Sync with parent value when it changes externally
  useEffect(() => {
    if (value !== localValue) {
      setLocalValue(value);
      if (debugMode) {
        console.log(`[ControlledTextarea:${fieldName}] External value changed:`, value);
      }
    }
  }, [value, debugMode, fieldName]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    
    if (debugMode) {
      console.log(`[ControlledTextarea:${fieldName}] Local change:`, newValue);
    }

    // Debounce parent update (5 seconds for better UX)
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      if (debugMode) {
        console.log(`[ControlledTextarea:${fieldName}] Debounced update to parent (5s delay):`, newValue);
      }
      onChange?.(newValue);
    }, 5000); // Increased from 1000ms to 5000ms
  };

  const handleBlur = (e) => {
    // Clear debounce timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Immediately update parent on blur
    const newValue = e.target.value;
    if (newValue !== value) {
      if (debugMode) {
        console.log(`[ControlledTextarea:${fieldName}] Blur update to parent:`, newValue);
      }
      onChange?.(newValue);
    }
    
    onBlur?.(e);
  };

  const handleFocus = (e) => {
    if (debugMode) {
      console.log(`[ControlledTextarea:${fieldName}] Focus gained`);
    }
  };

  return (
    <textarea
      {...props}
      ref={textareaRef}
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="off"
      spellCheck="false"
    />
  );
};

export default ControlledTextarea;
